const UNSAFE_RETURN_TO = /[\\\s%]/;
const PUBLIC_CODE = /^[A-Za-z0-9-]+$/;
const PUBLIC_ID = /^[A-Za-z0-9_-]+$/;

/** Public metadata only. /.well-known/webmcp is Sigma's discovery convention. */
export const WEBMCP_CATALOG = [
	{
		name: "sign_in",
		description:
			"Open sign-in for the user to unlock their identity. This tool never receives passwords or approves sign-in.",
		inputSchema: {
			type: "object",
			additionalProperties: false,
			properties: {
				returnTo: {
					type: "string",
					maxLength: 2048,
					description:
						"Optional same-site path beginning with a single /; external URLs are rejected.",
				},
			},
		},
	},
	{
		name: "sign_up",
		description:
			"Open identity creation for the user. Keys and encrypted backups are created in the browser by the user flow.",
		inputSchema: {
			type: "object",
			additionalProperties: false,
			properties: {},
		},
	},
	{
		name: "list_my_identities",
		description:
			"Read BAP identities linked to the currently signed-in user. Requires an active session.",
		inputSchema: {
			type: "object",
			additionalProperties: false,
			properties: {},
		},
	},
	{
		name: "open_identity",
		description:
			"Open an identity dashboard for the signed-in user; the account route enforces ownership.",
		inputSchema: {
			type: "object",
			additionalProperties: false,
			required: ["bapId"],
			properties: {
				bapId: {
					type: "string",
					minLength: 1,
					maxLength: 128,
					pattern: "^[A-Za-z0-9_-]+$",
				},
			},
		},
	},
	{
		name: "submit_device_code",
		description:
			"Open the human approval screen for an OAuth device code or agent request. Does not submit approval. Agent flow requires agentId.",
		inputSchema: {
			type: "object",
			additionalProperties: false,
			required: ["userCode"],
			properties: {
				userCode: {
					type: "string",
					minLength: 1,
					maxLength: 128,
					pattern: "^[A-Za-z0-9-]+$",
				},
				flow: {
					type: "string",
					enum: ["oauth", "agent"],
					description: "Defaults to oauth.",
				},
				agentId: {
					type: "string",
					minLength: 1,
					maxLength: 128,
					pattern: "^[A-Za-z0-9_-]+$",
				},
			},
			allOf: [
				{
					if: { properties: { flow: { const: "agent" } }, required: ["flow"] },
					// biome-ignore lint/suspicious/noThenProperty: JSON Schema conditional keyword, not a callable thenable.
					then: { required: ["agentId"] },
				},
			],
		},
	},
	{
		name: "open_api_docs",
		description: "Open the interactive API reference.",
		inputSchema: {
			type: "object",
			additionalProperties: false,
			properties: {},
		},
	},
	{
		name: "open_agent_discovery",
		description: "Read public agent capability and approval endpoint metadata.",
		inputSchema: {
			type: "object",
			additionalProperties: false,
			properties: {},
		},
	},
] as const;

type ToolArgs = Record<string, unknown>;
export interface WebMCPTool {
	name: string;
	description: string;
	inputSchema: object;
	execute: (args: ToolArgs) => Promise<unknown>;
}

export interface WebMCPContext {
	registerTool?: (
		tool: WebMCPTool,
		options?: { signal: AbortSignal }
	) => void | Promise<void>;
	unregisterTool?: (name: string) => void | Promise<void>;
	provideContext?: (context: {
		tools: WebMCPTool[];
	}) => undefined | (() => void);
	clearContext?: () => void;
}

/** Choose exactly one implemented API; partial or absent APIs never break startup. */
export function registerWebMcpTools(
	surface: {
		document?: { modelContext?: WebMCPContext };
		navigator?: { modelContext?: WebMCPContext };
	},
	tools: WebMCPTool[]
): () => void {
	const current = surface.document?.modelContext;
	const navigatorContext = surface.navigator?.modelContext;
	let context: WebMCPContext | undefined;
	if (typeof current?.registerTool === "function") {
		context = current;
	} else if (
		typeof navigatorContext?.registerTool === "function" &&
		typeof navigatorContext.unregisterTool === "function"
	) {
		context = navigatorContext;
	}
	if (context) {
		const selected = context;
		const lifetime = new AbortController();
		const owned: string[] = [];
		let closed = false;
		const unregister = (name: string) => {
			try {
				Promise.resolve(selected.unregisterTool?.(name)).catch(() => {
					/* Detached browser context. */
				});
			} catch {
				/* Detached browser context. */
			}
		};
		const cleanup = () => {
			if (closed) {
				return;
			}
			closed = true;
			// Current document API removes only this registration's tools via signal.
			lifetime.abort();
			for (const name of owned.splice(0)) {
				unregister(name);
			}
		};
		const registered = (name: string) => {
			if (closed) {
				unregister(name);
			} else {
				owned.push(name);
			}
		};
		for (const tool of tools) {
			try {
				const registration = selected.registerTool?.(tool, {
					signal: lifetime.signal,
				});
				if (registration) {
					registration.then(() => registered(tool.name), cleanup);
				} else {
					registered(tool.name);
				}
			} catch {
				cleanup();
				break;
			}
		}
		return cleanup;
	}
	const legacy = surface.navigator?.modelContext;
	if (typeof legacy?.provideContext !== "function") {
		return () => {
			/* No browser registration to release. */
		};
	}
	try {
		const dispose = legacy.provideContext({ tools });
		return () => {
			try {
				if (typeof dispose === "function") {
					dispose();
				} else if (typeof legacy.clearContext === "function") {
					legacy.clearContext();
				} else {
					legacy.provideContext?.({ tools: [] });
				}
			} catch {
				/* Browser context may already be detached. */
			}
		};
	} catch {
		return () => {
			/* No browser registration to release. */
		};
	}
}

function safeReturnTo(value: unknown): value is string {
	if (
		typeof value !== "string" ||
		value.length === 0 ||
		value.length > 2048 ||
		!value.startsWith("/")
	) {
		return false;
	}
	try {
		const decoded = decodeURIComponent(value);
		return (
			decoded.startsWith("/") &&
			!decoded.startsWith("//") &&
			!UNSAFE_RETURN_TO.test(decoded) &&
			new URL(decoded, "https://sigma.invalid").origin ===
				"https://sigma.invalid"
		);
	} catch {
		return false;
	}
}

function identifier(value: unknown, code = false): value is string {
	return (
		typeof value === "string" &&
		value.length > 0 &&
		value.length <= 128 &&
		(code ? PUBLIC_CODE : PUBLIC_ID).test(value)
	);
}

/** Dependencies are injected so tests exercise the same navigation and session guards. */
export function createWebMcpTools(deps: {
	push: (path: string) => void;
	getSession: () => Promise<{ data?: { user?: unknown } | null }>;
	fetch: (input: string, init?: RequestInit) => Promise<Response>;
}): WebMCPTool[] {
	const navigate = (path: string) => {
		deps.push(path);
		return { navigated: path };
	};
	const authenticated = async () =>
		Boolean((await deps.getSession()).data?.user);
	const unauthenticated = {
		error: "unauthenticated",
		message: "Sign in before using this tool.",
	};
	const read = async (path: string, credentials: RequestCredentials) => {
		const response = await deps.fetch(path, {
			credentials,
			signal: AbortSignal.timeout(10_000),
		});
		return response.ok
			? response.json()
			: { error: "request_failed", status: response.status };
	};
	const execute: Record<
		(typeof WEBMCP_CATALOG)[number]["name"],
		(args: ToolArgs) => unknown | Promise<unknown>
	> = {
		sign_in: (args) => {
			if (args.returnTo !== undefined && !safeReturnTo(args.returnTo)) {
				return { error: "invalid_return_to" };
			}
			return navigate(
				args.returnTo === undefined
					? "/login"
					: `/login?return_to=${encodeURIComponent(args.returnTo as string)}`
			);
		},
		sign_up: () => navigate("/signup"),
		list_my_identities: async () =>
			(await authenticated())
				? read("/api/user/bap-ids", "include")
				: unauthenticated,
		open_identity: async (args) => {
			if (!identifier(args.bapId)) {
				return { error: "invalid_bap_id" };
			}
			if (!(await authenticated())) {
				return unauthenticated;
			}
			return navigate(`/account/${encodeURIComponent(args.bapId)}`);
		},
		submit_device_code: async (args) => {
			if (!identifier(args.userCode, true)) {
				return { error: "invalid_user_code" };
			}
			if (
				args.flow !== undefined &&
				args.flow !== "oauth" &&
				args.flow !== "agent"
			) {
				return { error: "invalid_flow" };
			}
			if (args.flow === "agent" && !identifier(args.agentId)) {
				return { error: "invalid_agent_id" };
			}
			if (args.agentId !== undefined && !identifier(args.agentId)) {
				return { error: "invalid_agent_id" };
			}
			if (!(await authenticated())) {
				return unauthenticated;
			}
			const query = new URLSearchParams({ user_code: args.userCode });
			if (args.flow === "agent") {
				query.set("agent_id", args.agentId as string);
				return navigate(`/agent/approve?${query}`);
			}
			return navigate(`/device/approve?${query}`);
		},
		open_api_docs: () => navigate("/api/auth/reference"),
		open_agent_discovery: () =>
			read("/.well-known/agent-configuration", "omit"),
	};
	return WEBMCP_CATALOG.map((metadata) => ({
		...metadata,
		execute: async (args) => {
			if (
				!args ||
				typeof args !== "object" ||
				Array.isArray(args) ||
				Object.keys(args).some(
					(key) => !Object.hasOwn(metadata.inputSchema.properties, key)
				)
			) {
				return { error: "invalid_arguments" };
			}
			return await execute[metadata.name](args);
		},
	}));
}
